/**
 * Vue Router
 *
 * @package Muval Frontend Project
 * @copyright 2021 Muval Pty Ltd. All rights reserved.
 * @author Tim Maier
 */

import { createRouter, createWebHistory } from 'vue-router';
import { useTitle } from '@vueuse/core';
import * as Sentry from '@sentry/vue';
import { finishPageProgress, startPageProgress } from '@/plugins/page-progress';
import { clearPreloadReloadFlag, reloadOnVitePreloadFailure } from '@/utilities/vitePreloadRecovery';
import { CREW_ROUTES, homeRouteTarget, isRefusedByDenyRoles } from './roleAccess';

// Domains
import affiliateRoutes from './domains/affiliateRoutes';
import ancillaryRoutes from './domains/ancillaryRoutes';
import communicationRoutes from './domains/communicationRoutes';
import commsRoutes from './domains/commsRoutes';
import couponRoutes from './domains/couponRoutes';
import depotRoutes from './domains/depotRoutes';
import fleetRoutes from './domains/fleetRoutes';
import vesselRoutes from './domains/vesselRoutes';
import trailerRoutes from './domains/trailerRoutes';
import formRoutes from './domains/formRoutes';
import interstateRoutes from './domains/interstateRoutes';
import jobRoutes from './domains/jobRoutes';
import leadRoutes from './domains/leadRoutes';
import localPriceRoutes from './domains/localPriceRoutes';
import payoutRoutes from './domains/payoutRoutes';
import purchaseOrderRoutes from './domains/purchaseOrderRoutes';
import rateRoutes from './domains/rateRoutes';
import newsRoutes from './domains/newsRoutes';
import orderRoutes from './domains/orderRoutes';
import providerRoutes from './domains/providerRoutes';
import quoteRoutes from './domains/quoteRoutes';
import reportRoutes from './domains/reportRoutes';
import trackingRoutes from './domains/trackingRoutes';
import routesRoutes from './domains/routesRoutes';
import salesRoutes from './domains/salesRoutes';
import schedulerRoutes from './domains/schedulerRoutes';
import settingsRoutes from './domains/settingsRoutes';
import staffRoutes from './domains/staffRoutes';
import storageRoutes from './domains/storageRoutes';
import storageJobsRoutes from './domains/storageJobsRoutes';
import tenantsRoutes from './domains/tenantsRoutes';
import toolsRoutes from './domains/toolsRoutes';
import usersRoutes from './domains/usersRoutes';
import diarySchedulerRoutes from './domains/diarySchedulerRoutes';
import customerAutomationsRoutes from './domains/customerAutomationsRoutes';
import customerRoutes from './domains/customerRoutes';
import runsSchedulerRoutes from './domains/runsSchedulerRoutes';
import fieldStaffRoutes from './domains/fieldStaffRoutes';
import migrationRoutes from './domains/migrationRoutes';
import migratedDataRoutes from './domains/migratedDataRoutes';
import waybillRoutes from './domains/waybillRoutes';
import companyDiaryRoutes from './domains/companyDiaryRoutes';
import { useAppStore } from '../store/app';
import { useUserStore } from '@store/user';

const router = createRouter({
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
            };
        }
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    },
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            name: 'RootHome',
            meta: {
                requiresAuth: true,
            },
            beforeEnter: async (to, from, next) => {
                const userStore = useUserStore();
                const route = await userStore.resolvePostAuthRoute();
                next(route);
            },
        },
        {
            path: '/login',
            name: 'Login',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                hideForAuth: true,
                fullscreen: true,
                title: 'Login',
            },
            components: {
                default: () => import('../views/SignInView.vue'),
            },
        },
        {
            path: '/reset-password',
            name: 'ForgotPassword',
            props: {
                default: (route) => ({ token: route.query.token }),
            },
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                hideForAuth: true,
                fullscreen: true,
                title: 'Reset password',
            },
            components: {
                default: () => import('../views/ForgotPassword.vue'),
            },
        },
        {
            path: '/invite/:hash',
            name: 'AcceptInvite',
            props: {
                default: (route) => ({ hash: route.params.hash }),
            },
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
            },
            components: {
                default: () => import('../views/AcceptInviteView.vue'),
            },
        },
        {
            path: '/register',
            name: 'Register',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                hideForAuth: true,
                fullscreen: true,
                title: 'Register',
            },
            components: {
                default: () => import('../views/RegistrationView.vue'),
            },
        },
        {
            path: '/mfa/challenge',
            name: 'MfaChallenge',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                title: 'Verify your identity',
            },
            components: {
                default: () => import('../views/auth/MfaChallengePage.vue'),
            },
        },
        {
            path: '/mfa/setup',
            name: 'MfaSetup',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                title: 'Set up two-factor authentication',
            },
            components: {
                default: () => import('../views/auth/MfaSetupPage.vue'),
            },
        },
        {
            path: '/mfa/otp-challenge',
            name: 'OtpChallenge',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                title: 'Enter verification code',
            },
            components: {
                default: () => import('../views/auth/OtpChallengePage.vue'),
            },
        },
        {
            // Passwordless per-task crew share link. The share token resolves
            // its own tenant server-side (slug-less exchange), so the URL only
            // carries a cosmetic order ref + the token. fullscreen=true keeps
            // the page bare (no AuthenticatedLayout / nav / sidebar).
            path: '/task/:orderRef/:token',
            name: 'CrewTaskLink',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                title: 'Task',
            },
            components: {
                default: () => import('@/views/task/TaskLinkView.vue'),
            },
        },
        {
            // Crew (public share-link) full ICR editor. Same bare/no-auth shell
            // as the task page; the crew context + move seeding are handled by
            // CrewIcrProvider inside CrewIcrPage (meta.crew skips staff fetches).
            path: '/task/:orderRef/:token/icr',
            name: 'CrewIcrEdit',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                fullHeight: true,
                crew: true,
                icrView: 'edit',
                title: 'Inventory condition report',
            },
            components: {
                default: () => import('@/views/task/CrewIcrPage.vue'),
            },
        },
        {
            path: '/task/:orderRef/:token/icr/sign-off',
            name: 'CrewIcrSignOff',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                fullHeight: true,
                crew: true,
                icrView: 'signOff',
                title: 'ICR sign off',
            },
            components: {
                default: () => import('@/views/task/CrewIcrPage.vue'),
            },
        },
        {
            path: '/task/:orderRef/:token/icr/view-signed',
            name: 'CrewIcrViewSignedOff',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                fullHeight: true,
                crew: true,
                icrView: 'viewSigned',
                title: 'View signed off',
            },
            components: {
                default: () => import('@/views/task/CrewIcrPage.vue'),
            },
        },
        {
            path: '/select-organisation',
            name: 'SelectTenant',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                fullscreen: true,
                title: 'Select organisation',
            },
            components: {
                default: () => import('../views/SelectTenantView.vue'),
            },
        },
        {
            path: '/dashboard',
            name: 'Dashboard',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Dashboard',
            },
            components: {
                default: () => import('../views/dashboard/DashboardIndex.vue'),
            },
        },
        {
            path: '/home',
            name: 'Home',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                fullWidth: true,
                title: 'Home',
            },
            beforeEnter: () => homeRouteTarget(useUserStore()),
            components: {
                default: () => import('../views/dashboard/HomeIndex.vue'),
            },
        },
        {
            path: '/task/:id',
            name: 'TaskDetail',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                fullWidth: true,
            },
            components: {
                default: () => import('../views/task/TaskDetailView.vue'),
            },
        },
        {
            path: '/me',
            name: 'My account',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'My account',
            },
            components: {
                default: () => import('../views/MyAccountView.vue'),
            },
        },
        {
            path: '/search',
            name: 'Search',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Search',
            },
            components: {
                default: () => import('../views/search/GlobalSearch.vue'),
            },
        },
        {
            path: '/development',
            name: 'Development',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Development',
            },
            components: {
                default: () => import('../views/DevPage.vue'),
            },
        },
        {
            path: '/help',
            name: 'Help',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Need help?',
            },
            components: {
                default: () => import('@views/HelpPage.vue'),
            },
        },

        // Domain Routes
        ...affiliateRoutes,
        ...ancillaryRoutes,
        ...communicationRoutes,
        ...commsRoutes,
        ...couponRoutes,
        ...depotRoutes,
        ...fleetRoutes,
        ...vesselRoutes,
        ...trailerRoutes,
        ...formRoutes,
        ...interstateRoutes,
        ...jobRoutes,
        ...leadRoutes,
        ...localPriceRoutes,
        ...payoutRoutes,
        ...purchaseOrderRoutes,
        ...rateRoutes,
        ...newsRoutes,
        ...orderRoutes,
        ...providerRoutes,
        ...quoteRoutes,
        ...reportRoutes,
        ...trackingRoutes,
        ...routesRoutes,
        ...salesRoutes,
        ...schedulerRoutes,
        ...settingsRoutes,
        ...staffRoutes,
        ...storageRoutes,
        ...storageJobsRoutes,
        ...tenantsRoutes,
        ...toolsRoutes,
        ...usersRoutes,
        ...diarySchedulerRoutes,
        ...customerAutomationsRoutes,
        ...customerRoutes,
        ...runsSchedulerRoutes,
        ...fieldStaffRoutes,
        ...migrationRoutes,
        ...migratedDataRoutes,
        ...waybillRoutes,
        ...companyDiaryRoutes,
        {
            path: '/inbox/calls',
            name: 'InboxCalls',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Calls',
            },
            components: {
                default: () => import('../views/inbox/CallsIndex.vue'),
            },
        },
        {
            path: '/inbox/sms',
            name: 'InboxSms',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Inbox',
            },
            components: {
                default: () => import('../views/inbox/SmsIndex.vue'),
            },
        },
        {
            path: '/finance',
            name: 'Finance',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Payments',
            },
            components: {
                default: () => import('../views/finance/FinanceIndex.vue'),
            },
        },
        {
            path: '/transactions',
            name: 'Disputes',
            meta: {
                requiresAuth: true,
                allowRoles: ['SUPER', 'OWNER', 'FINANCE'],
                denyRoles: null,
                title: 'Transactions',
            },
            components: {
                default: () => import('../views/disputes/DisputeIndex.vue'),
            },
        },
        {
            path: '/communications',
            name: 'Communications',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/communication/CommunicationsIndex.vue'),
            },
        },
        {
            path: '/not_authorised',
            name: '403Page',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error403Page.vue'),
            },
        },
        {
            path: '/service_unavailable',
            name: '503Page',
            meta: {
                allowRoles: null,
                denyRoles: null,
                title: 'Service temporarily unavailable',
            },
            components: {
                default: () => import('@views/Error503Page.vue'),
            },
        },
        {
            path: '/not_found',
            name: '404Page',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error404Page.vue'),
            },
        },
        {
            path: '/order-not-found',
            name: 'OrderNotFound',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/OrderNotFound.vue'),
            },
        },
        {
            path: '/:pathMatch(.*)*',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error404Page.vue'),
            },
        },
    ],
});

router.beforeEach(async (to, from, next) => {
    startPageProgress(to, from);

    const app = useAppStore();

    // Find abort controller and abort all requests tied to it and then remove it from the store
    const foundAbortController = app.axiosAbortControllerList[from.path];

    if (foundAbortController && from.path != '/' && from.path != to.path) {
        foundAbortController.controller.abort();
        delete app.axiosAbortControllerList[from.path];
    }

    app.side = false;

    const currentUser = useUserStore();
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const hideForAuth = to.matched.some((record) => record.meta.hideForAuth);
    const isCentralRoute = to.matched.some((record) => record.meta.isCentralRoute);

    const deepestRecord = to.matched[to.matched.length - 1] ?? {};
    const allowRoles = deepestRecord.meta?.allowRoles ?? null;
    const denyRoles = deepestRecord.meta?.denyRoles ?? null;

    // CU-86d3hdwu4: roles can be momentarily unknowable (initial hydration in
    // flight, tenant switch, partial data payloads). Never rule on an empty
    // roles array for a role-gated route — wait for resolution first, bounded
    // so navigation can never hang.
    const needsRoles = (allowRoles?.length ?? 0) > 0 || (denyRoles?.length ?? 0) > 0;

    if (requiresAuth && currentUser.isAuthenticated && needsRoles && currentUser.currentRoles.length === 0) {
        // The cap only guards a pathologically hung promise — fetchUser always
        // settles on its own, so keep it generous enough for slow connections.
        const resolved = await Promise.race([
            currentUser.ensureRolesResolved(),
            new Promise((resolve) => setTimeout(resolve, 15000)),
        ]);

        // The reload dropped and the session was kept, so the roles were never
        // received. Stay on the current page rather than ruling 403 on roles we
        // don't have (CU-86d4a566a).
        if (resolved === null) {
            next(false);

            return;
        }
    }

    if (requiresAuth && !currentUser.isAuthenticated) {
        const loginPath = window.location.pathname;

        next({ name: 'Login', query: { from: loginPath } });
    } else if (requiresAuth && currentUser.isAuthenticated && isCentralRoute && !currentUser.isCentralContext) {
        next({ name: 'Dashboard' });
    } else if (hideForAuth && currentUser.isAuthenticated) {
        next({ name: 'Dashboard' });
    } else if (requiresAuth && currentUser.isAuthenticated && currentUser.isRestrictedFieldStaff()) {
        if (!CREW_ROUTES.includes(to.name)) {
            next({ name: 'Home' });
            return;
        }
        next();
        return;
    } else {
        // if roles are not defined, allow access
        if (allowRoles === null && denyRoles == null) {
            next();
            return;
        }

        // A denied role refuses only when the user holds no other role the route admits
        if (isRefusedByDenyRoles(currentUser.currentRoles, denyRoles)) {
            next({ name: '403Page' });
            return;
        }

        // if allow roles are defined, check if user has the role
        if (allowRoles && allowRoles.length > 0) {
            // Admin roles have access to everything
            if (currentUser.hasAnyRole(['OWNER', 'ADMIN'])) {
                next();
                return;
            }
            if (currentUser.hasAnyRole(allowRoles)) {
                // Allow access if user has the role allowed
                next();
                return;
            } else {
                // CU-86d3hdwu4 canary: after ensureRolesResolved() a denial with an
                // EMPTY roles array should be unreachable for real staff — if this
                // fires, the hydration race has regressed.
                if (currentUser.currentRoles.length === 0) {
                    Sentry.captureMessage('CU-86d3hdwu4 guard 403: roles unresolved at navigation', {
                        level: 'warning',
                        tags: { guard_403: 'CU-86d3hdwu4' },
                        extra: {
                            to: to.name ?? to.path,
                            dataNull: !currentUser.data,
                            hasTenants: !!currentUser.data?.tenants,
                            tenantId: currentUser.tenantId,
                        },
                    });
                }

                // Prevent access if user does not have the role allowed
                next({ name: '403Page' });
                return;
            }
        }

        // If no roles are matched, allow access
        next();
        return;
    }
});

// Set meta title
router.afterEach(({ meta }) => {
    finishPageProgress();

    // Successful navigation means assets loaded — allow a later preload
    // failure in this tab to recover again.
    clearPreloadReloadFlag();

    useTitle(`${meta?.title ? `${meta.title} - ` : ''}Movepro`);
});

// Vite CSS / dynamic-import preload failures surface here when the
// vite:preloadError listener did not recover (or was not yet registered).
router.onError((error) => {
    finishPageProgress();

    if (reloadOnVitePreloadFailure(error)) {
        console.warn('vite preload/chunk failure — reloading', error);
    }
});

// Clean up ICR localStorage when leaving ICR route group
const ICR_ROUTE_NAMES = new Set(['OrderView', 'OrderInventoryConditionReport', 'IcrSignOff', 'IcrViewSignedOff']);

router.afterEach((to, from) => {
    if (ICR_ROUTE_NAMES.has(from.name) && !ICR_ROUTE_NAMES.has(to.name)) {
        try {
            const keys = Object.keys(window.localStorage);
            keys.forEach((key) => {
                if (key.startsWith('active_stage:') || key.startsWith('icr-room-selection:')) {
                    window.localStorage.removeItem(key);
                }
            });
        } catch {
            // Ignore localStorage errors
        }
    }
});

export default router;
